import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuthStore } from '../stores/auth';

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

export type SubscribeResult =
  | { ok: true }
  | { ok: false; reason: 'not-supported' | 'not-authenticated' | 'denied' | 'error'; message: string };

function isStandalonePWA(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}

export function usePushNotifications() {
  const { isAuthenticated } = useAuthStore();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);

      navigator.serviceWorker.ready.then(async (registration) => {
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      });
    }
  }, []);

  const subscribe = useCallback(async (): Promise<SubscribeResult> => {
    if (!isAuthenticated) {
      return { ok: false, reason: 'not-authenticated', message: 'Devi essere autenticato' };
    }

    if (!isSupported) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS && !isStandalonePWA()) {
        return {
          ok: false,
          reason: 'not-supported',
          message: 'Per ricevere notifiche su iPhone, installa la PWA: tocca il pulsante Condividi e poi "Aggiungi alla schermata Home"',
        };
      }
      return {
        ok: false,
        reason: 'not-supported',
        message: 'Le notifiche push non sono supportate su questo browser',
      };
    }

    setIsLoading(true);

    try {
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        return {
          ok: false,
          reason: 'denied',
          message: permissionResult === 'denied'
            ? 'Notifiche bloccate. Vai in Impostazioni > HomeFinder > Notifiche per abilitarle'
            : 'Permesso notifiche non concesso. Riprova e tocca "Consenti"',
        };
      }

      const { publicKey } = await api.getVapidKey();

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      const subscriptionJson = subscription.toJSON();
      await api.subscribePush({
        endpoint: subscriptionJson.endpoint!,
        keys: {
          p256dh: subscriptionJson.keys!.p256dh,
          auth: subscriptionJson.keys!.auth,
        },
      });

      setIsSubscribed(true);
      return { ok: true };
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return {
        ok: false,
        reason: 'error',
        message: 'Errore durante la registrazione delle notifiche. Riprova.',
      };
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, isAuthenticated]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported || !isAuthenticated) {
      return false;
    }

    setIsLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await api.unsubscribePush();
      }

      setIsSubscribed(false);
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, isAuthenticated]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
  };
}
